import React, { createContext, useContext } from "react";
import type { RenderNode } from "../../../util/src/layout-compiler/types.js";
import type { InjectedData } from "../../../util/src/layout/types.js";

export type RegisterNodeElement = (
  key: string,
  element: HTMLElement | null,
  node: RenderNode,
) => void;

/** Fires an onClick Behavior on any layout element, scoped to its rendered instance. */
export type DispatchCustomDisciplineClick = (
  nodeId: string,
  nodeKey?: string,
) => void;

/** Reports a user edit on a `customDisciplineInput` field back to the host page. */
export type OnCustomDisciplineInputChange = (
  fieldKey: string,
  value: string,
) => void;

type CompiledLayoutRuntimeContextValue = {
  registerNodeElement?: RegisterNodeElement;
  runtimeElementByKey?: Map<string, HTMLElement>;
  renderNodeByKey?: Map<string, RenderNode>;
  canvasElement?: HTMLElement | null;
  dispatchCustomDisciplineClick?: DispatchCustomDisciplineClick;
  onCustomDisciplineInputChange?: OnCustomDisciplineInputChange;
  /** Injected layout data used for nested layout tooltips. */
  runtimeData?: InjectedData;
};

const CompiledLayoutRuntimeContext =
  createContext<CompiledLayoutRuntimeContextValue>({});

/**
 * Returns the DOM registration callback for compiled layout runtime effects.
 */
export function useRegisterNodeElement(): RegisterNodeElement | undefined {
  return useContext(CompiledLayoutRuntimeContext).registerNodeElement;
}

/**
 * Returns the runtime DOM map keyed by compiled node key/id.
 */
export function useRuntimeElementByKey(): Map<string, HTMLElement> | undefined {
  return useContext(CompiledLayoutRuntimeContext).runtimeElementByKey;
}

/**
 * Returns compiled render nodes indexed by runtime key.
 */
export function useRenderNodeByKey(): Map<string, RenderNode> | undefined {
  return useContext(CompiledLayoutRuntimeContext).renderNodeByKey;
}

/**
 * Returns the compiled layout canvas element used for clip-path sync.
 */
export function useRuntimeCanvasElement(): HTMLElement | null | undefined {
  return useContext(CompiledLayoutRuntimeContext).canvasElement;
}

/**
 * Returns the dispatcher for any layout element's onClick Behavior.
 */
export function useDispatchCustomDisciplineClick():
  | DispatchCustomDisciplineClick
  | undefined {
  return useContext(CompiledLayoutRuntimeContext).dispatchCustomDisciplineClick;
}

/**
 * Returns the callback used by `customDisciplineInput` nodes to report user
 * edits back to the host page's `inputValues` state.
 */
export function useOnCustomDisciplineInputChange():
  | OnCustomDisciplineInputChange
  | undefined {
  return useContext(CompiledLayoutRuntimeContext).onCustomDisciplineInputChange;
}

/**
 * Returns the injected layout data for the current compiled renderer instance.
 */
export function useCompiledLayoutRuntimeData(): InjectedData | undefined {
  return useContext(CompiledLayoutRuntimeContext).runtimeData;
}

type CompiledLayoutRuntimeProviderProps = {
  registerNodeElement?: RegisterNodeElement;
  runtimeElementByKey?: Map<string, HTMLElement>;
  renderNodeByKey?: Map<string, RenderNode>;
  canvasElement?: HTMLElement | null;
  dispatchCustomDisciplineClick?: DispatchCustomDisciplineClick;
  onCustomDisciplineInputChange?: OnCustomDisciplineInputChange;
  runtimeData?: InjectedData;
  children: React.ReactNode;
};

/**
 * Supplies DOM element registration for animation and clip-path sync.
 */
export function CompiledLayoutRuntimeProvider({
  registerNodeElement,
  runtimeElementByKey,
  renderNodeByKey,
  canvasElement,
  dispatchCustomDisciplineClick,
  onCustomDisciplineInputChange,
  runtimeData,
  children,
}: CompiledLayoutRuntimeProviderProps) {
  return (
    <CompiledLayoutRuntimeContext.Provider
      value={{
        registerNodeElement,
        runtimeElementByKey,
        renderNodeByKey,
        canvasElement,
        dispatchCustomDisciplineClick,
        onCustomDisciplineInputChange,
        runtimeData,
      }}
    >
      {children}
    </CompiledLayoutRuntimeContext.Provider>
  );
}
